import { useSelector } from 'react-redux';
import { RootState } from '../../../data/store';
import { useState } from 'react';
import { ProjectCategories } from '../../../data/models/project';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ErrorIcon from '@mui/icons-material/Error';
import { styled } from 'styled-components';
import Tabs from '../../common/Tabs';
import { getIconFromGoogleMimeType } from '../../../utils/company-utils';
import { FlexCenterRow } from '../../common/layout/Content';
import GreenCheckIcon from '../../common/GreenCheckIcon';
import Paper from '../../common/layout/Paper';

const ProjectTemplates = (props) => {
	const {
		project: { projectInitializationFileStatuses },
	} = useSelector((state: RootState) => state.projectState);

	const [selectedPanel, setSelectedPanel] = useState(
		ProjectCategories.CREATIVE.id
	);

	const panels = [
		{
			label: (
				<FlexCenterRow>
					Creative Templates{' '}
					{projectInitializationFileStatuses[
						ProjectCategories.CREATIVE.id
					]?.status === 'SUCCESS' ? (
						<StyledGreenCheckIcon />
					) : (
						<StyledScheduleIcon />
					)}
				</FlexCenterRow>
			),
			component: (
				<TemplatesList
					category={ProjectCategories.CREATIVE.display}
					templates={Object.values(
						projectInitializationFileStatuses[
							ProjectCategories.CREATIVE.id
						]?.list || {}
					).map((t) => t)}
				/>
			),
			onclick: () => setSelectedPanel(ProjectCategories.CREATIVE.id),
			key: ProjectCategories.CREATIVE.id,
		},
		{
			label: (
				<FlexCenterRow>
					Production Templates{' '}
					{projectInitializationFileStatuses[
						ProjectCategories.PRODUCTION.id
					]?.status === 'SUCCESS' ? (
						<StyledGreenCheckIcon />
					) : (
						<StyledScheduleIcon />
					)}
				</FlexCenterRow>
			),
			component: (
				<TemplatesList
					category={ProjectCategories.PRODUCTION.display}
					templates={Object.values(
						projectInitializationFileStatuses[
							ProjectCategories.PRODUCTION.id
						]?.list || {}
					).map((t) => t)}
				/>
			),
			onclick: () => setSelectedPanel(ProjectCategories.PRODUCTION.id),
			key: ProjectCategories.PRODUCTION.id,
		},
		{
			label: (
				<FlexCenterRow>
					Post-Production Templates{' '}
					{projectInitializationFileStatuses[
						ProjectCategories.POST_PRODUCTION.id
					]?.status === 'SUCCESS' ? (
						<StyledGreenCheckIcon />
					) : (
						<StyledScheduleIcon />
					)}
				</FlexCenterRow>
			),
			component: (
				<TemplatesList
					category={ProjectCategories.POST_PRODUCTION.display}
					templates={Object.values(
						projectInitializationFileStatuses[
							ProjectCategories.POST_PRODUCTION.id
						]?.list || {}
					).map((t) => t)}
				/>
			),
			onclick: () =>
				setSelectedPanel(ProjectCategories.POST_PRODUCTION.id),
			key: ProjectCategories.POST_PRODUCTION.id,
		},
		{
			label: (
				<FlexCenterRow>
					Legal Templates{' '}
					{projectInitializationFileStatuses[
						ProjectCategories.LEGAL_AND_CONTRACT.id
					]?.status === 'SUCCESS' ? (
						<StyledGreenCheckIcon />
					) : (
						<StyledScheduleIcon />
					)}
				</FlexCenterRow>
			),
			component: (
				<TemplatesList
					category={ProjectCategories.LEGAL_AND_CONTRACT.display}
					templates={Object.values(
						projectInitializationFileStatuses[
							ProjectCategories.LEGAL_AND_CONTRACT.id
						]?.list || {}
					).map((t) => t)}
				/>
			),
			onclick: () =>
				setSelectedPanel(ProjectCategories.LEGAL_AND_CONTRACT.id),
			key: ProjectCategories.LEGAL_AND_CONTRACT.id,
		},
	];

	return (
		<PaperContainer gradientBorder>
			<TemplateSelectorContainer>
				<Tabs panels={panels} currentPath={selectedPanel} />
			</TemplateSelectorContainer>
		</PaperContainer>
	);
};

export default ProjectTemplates;

const TemplatesList = (props) => {
	const { templates, category } = props;

	if (!templates.length) {
		return (
			<NoTemplatesTextContainer>
				No {category} Templates currently exist for this project.
			</NoTemplatesTextContainer>
		);
	}

	return (
		<TemplatesListContainer>
			{templates.map((t) => (
				<TemplateRow key={t.templateName} template={t} />
			))}
		</TemplatesListContainer>
	);
};

const TemplatesListContainer = styled.div`
	max-height: calc(100vh - 350px);
	overflow-y: auto;
	padding-right: 10px;
`;

const NoTemplatesTextContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 50px;
`;

const TemplateRow = (props) => {
	const { template } = props;
	const icon = getIconFromGoogleMimeType(template.mimeType);

	return (
		<TemplateRowContainer>
			<FlexCenterRow>
				<img
					style={{
						height: 20,
						marginRight: 10,
						objectFit: 'cover',
					}}
					src={icon.icon}
					alt={icon.alt}
				/>
				{template.templateName}
			</FlexCenterRow>
			<FlexCenterRow>
				<StatusIconContainer>
					{template.status?.includes('ERROR') && (
						<StyledErrorIcon className="fade-in" />
					)}
					{template.status === 'SUCCESS' ? (
						<StyledGreenCheckIcon className="fade-in" />
					) : (
						<StyledScheduleIcon className="fade-in" />
					)}
				</StatusIconContainer>
			</FlexCenterRow>
		</TemplateRowContainer>
	);
};

const PaperContainer = styled(Paper)`
	margin-top: 10px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	flex: 1;
`;

const StatusIconContainer = styled.div`
	.fade-in {
		animation: fadeIn 0.2s ease-in forwards;
	}
	display: flex;
	align-items: center;
	justify-content: center;
`;

const StyledGreenCheckIcon = styled(GreenCheckIcon)`
	font-size: 20px !important;
	margin-left: 5px;
`;

const StyledErrorIcon = styled(ErrorIcon)`
	font-size: 20px !important;
	margin-left: 5px;
	color: ${({ theme }) => theme.palette.error.main};
`;

const StyledScheduleIcon = styled(ScheduleIcon)`
	color: ${({ theme }) => theme.palette.action.pending};
	font-size: 20px !important;
	margin-left: 5px;
`;

const TemplateSelectorContainer = styled.div`
	display: flex;
	flex-direction: column;
	padding: 0px 10px;
	flex: 1;
`;

const TemplateRowContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 10px 0;
	border-bottom: 1px solid ${({ theme }) => theme.palette.text.divider};
`;
